import type { StackTowerConfig } from './rules.ts';
import type { TowerRunState } from './tower-state.ts';

/**
 * The 60-second head-to-head rule set (C015), built as configuration/helpers
 * around the same deterministic placement/movement as Solo Endless — never a
 * fork of it (spec §96). Two pieces, both pure and server-verifiable:
 *
 *  - `resetTowerKeepingScore` — the timed miss behaviour (spec §103): a miss
 *    does not end the run, it resets the tower to its starting footprint while
 *    keeping the accumulated score. The match ends on the authoritative timer,
 *    not on a miss.
 *  - `decideHeadToHead` — the winner + tiebreaker decision (spec §104), the
 *    one piece the server (the `StackTowerMatch` Durable Object) is the
 *    authority for.
 */

/** The flagship timed-mode duration. The server derives the whole window from one shared `matchStartMs` (spec §108/§110). */
export const TIMED_MATCH_DURATION_MS = 60_000;

/**
 * Owner-reported exploit (C017, post-launch): with a miss costing nothing but
 * the tower geometry, a player can spam-click blindly through the wide, easy
 * starting footprint — a chunk of those clicks miss, but misses are free, so
 * only the lucky hits matter, for real points with no real skill or risk.
 * Missing any of the first `EARLY_MISS_PENALTY_BLOCKS` placements of a climb
 * now costs a point (`EARLY_MISS_PENALTY_POINTS`), so blind spam-clicking the
 * easy base is actively costly instead of free. Solo Endless and Multiplayer
 * Endless need no equivalent — a miss there already ends the run outright,
 * which is already a far bigger cost than one point.
 */
export const EARLY_MISS_PENALTY_BLOCKS = 3;
export const EARLY_MISS_PENALTY_POINTS = 1;

/**
 * Applies a timed-mode miss: the tower resets to its full starting footprint
 * and the moving block back to full width, the perfect streak breaks, but the
 * **accumulated score and cumulative match stats are kept** and play continues
 * (spec §103). A miss therefore costs time, progression and streak — not the
 * match. Pure; the caller supplies `nowMs` (the new block's spawn time).
 *
 * `climbBlocks` resets to 0 along with the tower geometry — deliberately,
 * not an oversight: it's what drives the score multiplier
 * (`multiplierForClimb`), so a miss also costs whatever multiplier tier the
 * climb had reached. Without this, the tower reset would be free from a
 * scoring standpoint and the rational move once the margin gets thin would
 * be to miss on purpose and farm the easy wide base instead of pushing
 * higher — the multiplier only disincentivises that if losing height
 * actually loses something.
 *
 * The early-miss penalty checks `state.climbBlocks` — the number of blocks
 * *already* landed in the climb this miss just ended — against
 * `EARLY_MISS_PENALTY_BLOCKS` BEFORE it gets reset to 0: missing on your 1st,
 * 2nd or 3rd placement of a climb (climbBlocks 0, 1 or 2 at the moment of the
 * miss) costs a point; missing your 4th or later does not. Floored at 0 —
 * this is a deterrent, not a way to end up with a negative score.
 */
export function resetTowerKeepingScore(state: TowerRunState, config: StackTowerConfig, nowMs: number): TowerRunState {
  const half = config.startingBlockWidth / 2;
  const centre = config.trackWidth / 2;
  const penalised = state.climbBlocks < EARLY_MISS_PENALTY_BLOCKS;
  return {
    ...state,
    phase: 'playing',
    topFootprint: { min: centre - half, max: centre + half },
    movingBlockWidth: config.startingBlockWidth,
    spawnedAtMs: nowMs,
    sweepStartEdge: 'low',
    perfectStreak: 0,
    climbBlocks: 0,
    finalOutcome: null,
    score: penalised ? Math.max(0, state.score - EARLY_MISS_PENALTY_POINTS) : state.score,
    // maxPerfectStreak, perfectCount, placementCount, smallestOverlapSize and
    // offsetSum are all carried through unpenalised — they are cumulative
    // stats over the match, not the score itself.
  };
}

/** The tiebreaker inputs a head-to-head result compares — a subset of `StackTowerRunResult`, which satisfies it directly. */
export interface HeadToHeadPlayerResult {
  readonly score: number;
  /** Final top-block width as a fraction of the start — spec §104's "greater remaining top-block width / area". */
  readonly finalWidthFraction: number;
  readonly perfectCount: number;
  readonly maxPerfectStreak: number;
  /** Lower is more precise — spec §104's "lower average placement error". */
  readonly averagePlacementOffset: number;
}

export type HeadToHeadOutcome = 'a' | 'b' | 'draw';

/**
 * Decides a timed head-to-head match from both players' final states, applying
 * the spec §104 tiebreaker order exactly: higher score, then greater final
 * top-block width, then more perfects, then a longer max perfect streak, then a
 * lower average placement offset, then a draw. Returns which side won (`'a'`/
 * `'b'`) or `'draw'`. This is the server's authoritative decision — the client
 * never decides who won.
 */
export function decideHeadToHead(a: HeadToHeadPlayerResult, b: HeadToHeadPlayerResult): HeadToHeadOutcome {
  // Rungs where higher wins, in priority order.
  const higherWins: readonly (keyof HeadToHeadPlayerResult)[] = [
    'score',
    'finalWidthFraction',
    'perfectCount',
    'maxPerfectStreak',
  ];
  for (const key of higherWins) {
    if (a[key] > b[key]) return 'a';
    if (a[key] < b[key]) return 'b';
  }
  // Final rung: a lower average offset (more precise) wins.
  if (a.averagePlacementOffset < b.averagePlacementOffset) return 'a';
  if (a.averagePlacementOffset > b.averagePlacementOffset) return 'b';
  return 'draw';
}
